import type { TFunction } from 'i18next';
import type { ReasoningStep } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';

const Q = I18nNamespace.QUERIES;

export function buildAdditionalResultsSteps(
    t: TFunction,
    eventCount: number,
    sourceCount: number,
    isLoading: boolean,
    query: string | null,
): ReasoningStep[] {
    const steps: ReasoningStep[] = [
        { text: t('reasoning.searchingAdditional', { ns: Q }) },
    ];
    const eventLabel = t('reasoning.event', { count: eventCount, ns: Q });
    const sourceLabel = t('reasoning.source', { count: sourceCount, ns: Q });

    if (!isLoading && eventCount === 0) {
        steps.push({ text: t('reasoning.noAdditionalEvents', { ns: Q }) });
    } else if (eventCount > 0 && isLoading) {
        steps.push({
            text: t('reasoning.displayingAdditionalSearching', {
                ns: Q,
                count: eventCount,
                label: eventLabel,
            }),
        });
    } else if (eventCount > 0 && !isLoading) {
        steps.push({
            text: t('reasoning.displayingAdditionalDone', {
                ns: Q,
                count: eventCount,
                label: eventLabel,
                sourceCount,
                sourceLabel,
                query,
            }),
        });
    }

    return steps;
}

export function buildPrimarySteps(
    t: TFunction,
    eventCount: number,
    sourceCount: number,
    identityCount: number,
    isLoading: boolean,
    query: string | null,
    hasAttachment: boolean,
    analyzedQueries: unknown,
): ReasoningStep[] {
    const steps: ReasoningStep[] = [];

    const analyzingKey = hasAttachment
        ? query
            ? 'reasoning.analyzingQueryWithAttachment'
            : 'reasoning.analyzingQueryWithOnlyAttachment'
        : 'reasoning.analyzingQuery';
    steps.push({ text: t(analyzingKey, { ns: Q, query }) });

    if (
        analyzedQueries &&
        isLoading &&
        eventCount === 0 &&
        identityCount === 0
    ) {
        steps.push({ text: t('reasoning.searchingRelevantEvents', { ns: Q }) });
    }

    if (
        analyzedQueries &&
        !isLoading &&
        eventCount === 0 &&
        identityCount === 0
    ) {
        const key = query?.trim()
            ? 'reasoning.noResultsForQuery'
            : 'reasoning.noResults';
        steps.push({ text: t(key, { ns: Q, query: query?.trim() }) });
    } else if (eventCount > 0 || identityCount > 0) {
        const eventLabel = t('reasoning.event', { count: eventCount, ns: Q });
        const sourceLabel = t('reasoning.source', {
            count: sourceCount,
            ns: Q,
        });
        const identityLabel = t('reasoning.identity', {
            count: identityCount,
            ns: Q,
        });

        const partsList: string[] = [];
        if (identityCount > 0) {
            partsList.push(
                t('reasoning.identityCount', {
                    ns: Q,
                    count: identityCount,
                    label: identityLabel,
                }),
            );
        }
        if (eventCount > 0) {
            const eventsKey = isLoading
                ? 'reasoning.eventsFromSources'
                : hasAttachment
                  ? query
                      ? 'reasoning.eventsFromSourcesForQueryWithAttachment'
                      : 'reasoning.eventsFromSourcesForQueryWithOnlyAttachment'
                  : 'reasoning.eventsFromSourcesForQuery';
            partsList.push(
                t(eventsKey, {
                    ns: Q,
                    eventCount,
                    eventLabel,
                    sourceCount,
                    sourceLabel,
                    query,
                }),
            );
        }

        const parts = partsList.join(', ');
        const displayKey = isLoading
            ? 'reasoning.displayingSearching'
            : 'reasoning.displaying';
        steps.push({ text: t(displayKey, { ns: Q, parts }) });
    }

    return steps;
}
